// Section · The BIG6 philosophies — STRIKZA's intellectual foundation.
//
// Source of truth: STRIKZA_The_BIG6_Philosophies.docx
//
// What this section is for:
//   - The "show your homework" moment. STRIKZA isn't applying generic
//     AI to football — it stands on six named market thinkers whose
//     mechanisms are encoded in the engine. Surfacing them on the
//     landing distinguishes STRIKZA from every "AI tipster" that
//     can't say where their edge comes from.
//   - Each pillar is a one-line summary lifted from the doctrine,
//     credited to the practitioner whose method seeded it.
//   - This is editorial substance. Numbers can be faked; a credited
//     market-philosophy stack cannot.

interface Big6Pillar {
  readonly key: string;
  readonly name: string;
  readonly tag: string;
  readonly summary: string;
}

const BIG6: readonly Big6Pillar[] = [
  {
    key: "benter",
    name: "Benter",
    tag: "The pricing engine",
    summary:
      "Generate STRIKZA's own fair price first via Poisson + xG; consult the bookmaker line second. The gap is the entire business.",
  },
  {
    key: "voulgaris",
    name: "Voulgaris",
    tag: "CLV as the north star",
    summary:
      "Win rate is noise. Closing-line value is the only honest pre-result proof of edge. Every signal is tracked vs Pinnacle close.",
  },
  {
    key: "walters",
    name: "Walters",
    tag: "Market intelligence",
    summary:
      "Line movement is itself a signal. STRIKZA classifies steam moves, public drift, and trap lines — the market context, not just the price.",
  },
  {
    key: "ranogajec",
    name: "Ranogajec",
    tag: "Volume + Kelly discipline",
    summary:
      "A 1-2% edge applied with disciplined Kelly sizing across thousands of events compounds to certainty. Aggregate user volume is the syndicate.",
  },
  {
    key: "bloom",
    name: "Bloom",
    tag: "Surface-area selection",
    summary:
      "Don't compete in efficient markets. Attack lazy lines — lower leagues, early windows, GPL/NPFL — using Pinnacle close as the calibration benchmark.",
  },
  {
    key: "benham",
    name: "Benham",
    tag: "The data flywheel",
    summary:
      "Every fixture invests in future accuracy through CLV/Brier/Fixture-DNA accumulation. A competitor launching today can never close the head start.",
  },
];

export function Big6Strip(): JSX.Element {
  return (
    <section
      id="big6"
      className="sz-l-section"
      style={{
        position: "relative",
        padding: "140px 0 160px",
        borderTop: "1px solid var(--border)",
      }}
    >
      <div className="container" style={{ position: "relative" }}>
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            marginBottom: 60,
          }}
        >
          <div>
            <span className="bracket">Section · The BIG6</span>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              We didn&apos;t invent this.{" "}
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>We productised it.</em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              Six market thinkers — Benter, Voulgaris, Walters, Ranogajec, Bloom, Benham — proved
              the methods. STRIKZA encodes their mechanisms into one operating system, runs them
              continuously across every fixture, and seals the result in a public ledger every
              night. The edges are old. The execution is new.
            </p>
          </div>
        </div>

        <div
          className="sz-l-big6-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
          }}
        >
          {BIG6.map((p, i) => (
            <div
              key={p.key}
              style={{
                position: "relative",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                padding: "28px 24px",
                minHeight: 220,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  color: "var(--accent)",
                  textTransform: "uppercase",
                  marginBottom: 14,
                }}
              >
                BIG6 · 0{i + 1}
              </div>
              <div
                className="display"
                style={{
                  fontSize: 30,
                  color: "var(--text)",
                  letterSpacing: "0.005em",
                  marginBottom: 4,
                }}
              >
                {p.name}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--accent-dim)",
                  fontStyle: "italic",
                  marginBottom: 18,
                }}
                className="serif"
              >
                {p.tag}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  lineHeight: 1.55,
                  marginTop: "auto",
                }}
              >
                {p.summary}
              </div>
            </div>
          ))}
        </div>

        <p
          className="serif"
          style={{
            marginTop: 40,
            fontSize: 16,
            color: "var(--text-muted)",
            fontStyle: "italic",
            textAlign: "center",
            maxWidth: 640,
            marginInline: "auto",
            lineHeight: 1.5,
          }}
        >
          Methodology compounds. Picks expire. STRIKZA sells tools, not picks — the Unabated
          principle, applied to West African football first and global probability second.
        </p>
      </div>
    </section>
  );
}
